import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, seam, cable, limb2, joint, ram, mergeBones, stages, boneG } from './kit';
/* ============================================================
   KAISER 皇帝 —— 最終・機甲
   ============================================================

   剪影：玩家機體的剪影 × 6.5 倍（同一份比例表）
   階段：'P1 立姿・遠距砲擊（橙）','P2 甩甲＋單膝跪地・從行走兵器變成固定砲台', 'P3 上半身脫離・浮空（下半身癱在原地）'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ══════════════════════════════════════════════════════════════
   最終 BOSS 24–28 · 四條血量

   「震撼」不等於「更大」。大只是第一分鐘的事。
   這五隻各自負責一種**只有最終 BOSS 才付得起代價的東西**：

     KAISER     用玩家自己的造型語彙 —— 那是你，穿著敵人的顏色
     CHIMÄRE    八個剪影家族接在同一根脊椎上 —— 你殺過的東西全在牠身上
     REQUIEM    船身嵌滿被擊毀的玩家機甲 —— 唯一一次，綠色不屬於你
     WELTACHSE  相機框不下 —— 你打的是它的一小段
     NICHTS     機甲形狀的空洞 —— 它不是東西，是東西不在

   三條血量的造型端寫成 s1/s2 的線性組合，跟其他三階段 BOSS 同一套機制。
   （原本做成四階段，但第四拍在五隻身上都只是「再大聲一次」，沒有換掉玩家的問題。
     砍掉之後每一階段的轉折都更清楚 —— 階段數不是預算，是敘事節拍。）
   ══════════════════════════════════════════════════════════════ */

/* ── BOSS-24 KAISER 皇帝 ── 24 單位高。**比照玩家機體逐項重建**。
   `KS = 6.5` 是唯一的縮放常數：玩家機體 3.7 單位 × 6.5 = 24。
   底下每一個座標都是機體檔的原始數值乘上 KS —— 髖高 2.00、軀幹 2.22、
   肩承座 ±0.96、頭偏左 0.14、髖線轉 0.20 rad、軀幹前傾 0.14、7.4 頭身。
   它不是「一隻長得像機甲的敵人」，是**同一份比例表**。

   craft 一併補齊（這些是敵人檔一直缺的）：
     · 兩段式肢體 limb2()：上段厚、下段收
     · joint(cap=true)：關節軸端突出到裝甲外
     · ram()：膝、肘的液壓行程
     · cable()：腰縫外面垂下來的線束
     · seam() / rib()：凹縫與亮稜成對出現
     · 胸與骨盆之間**看得穿的縫** —— 實心塊與機構的分界
   《86》的參考在兩處：頸上獨立的感測器頭，以及右臂那根不合理長的砲管（Morpho）。 */
var KS = 6.5;
export function buildKaiser(){
  var GLA = glowMat(TINT.amb.hex, 2.2), GL = glowMat(TINT.red.hex, 2.0),
      GLV = glowMat(TINT.vio.hex, 2.6);
  var S = KS, A = M.armorF, D = M.dark, T = M.trim;
  var root = new THREE.Group(), i, sg, legs = [], arms = [], shed = [], pods = [];
  var body = boneG(root, 0, 2.00*S, 0);

  /* ── 下半身：髖線轉 11°，與肩線交叉 ── */
  var lower = boneG(body, 0, 0, 0);
  lower.rotation.y = 0.20;
  var pel = boneG(lower, 0, -0.07*S, 0);
  pel.add(bevel(D, 1.02*S, 0.44*S, 0.88*S, [0, 0.06*S, 0]));
  pel.add(plate(A, 0.32*S, 0.50*S, 0.76*S, [ 0.60*S, 0.02*S, 0], 0, -0.18, -0.10));
  pel.add(plate(A, 0.32*S, 0.50*S, 0.76*S, [-0.60*S, 0.02*S, 0], 0,  0.18,  0.10));
  pel.add(plate(A, 0.88*S, 0.46*S, 0.22*S, [0, -0.18*S,  0.44*S],  0.36));
  pel.add(plate(A, 0.96*S, 0.52*S, 0.22*S, [0, -0.14*S, -0.46*S], -0.30));
  pel.add(rib  (A, 0.08*S, 0.08*S, 0.84*S, [ 0.63*S, 0.26*S, 0], 0, -0.18));
  pel.add(rib  (A, 0.08*S, 0.08*S, 0.84*S, [-0.63*S, 0.26*S, 0], 0,  0.18));
  pel.add(seam (0.92*S, 0.05*S, 0.12*S, [0, 0.28*S, 0.42*S], 0.20));
  for (i = 0; i < 2; i++){
    sg = i ? -1 : 1;
    var hip = boneG(lower, sg*0.48*S, 0, 0);
    hip.rotation.y = -sg*0.06;
    hip.add(joint(0.23*S, [0,0,0], true));                      // 髖：軸端外露
    var th = boneG(hip, 0, 0, 0);
    th.add(limb2(D, 0.47*S, 1.08*S, [0,0,0]));                  // 股：上段厚、下段收
    th.add(plate(A, 0.52*S, 0.60*S, 0.27*S, [0, -0.30*S, 0.25*S], -0.12));
    th.add(seam (0.50*S, 0.05*S, 0.10*S, [0, -0.58*S, 0.27*S], -0.12));
    th.add(rib  (A, 0.07*S, 0.76*S, 0.07*S, [sg*0.25*S, -0.40*S, 0.13*S], -0.08));
    th.add(ram  ([ 0.20*S, -0.74*S, 0.21*S], 0.62*S, 0.10*S, 0.08*S, [-0.14,0,0]));
    th.add(ram  ([-0.20*S, -0.74*S, 0.21*S], 0.62*S, 0.10*S, 0.08*S, [-0.14,0,0]));
    var kn = boneG(th, 0, -1.02*S, 0);
    kn.add(joint(0.20*S, [0,0,0], true));                       // 膝：軸端外露
    var kg = mesh(G.oct, GL, [0.26*S, 0.26*S, 0.26*S], [0, 0, -0.10*S]);
    kg.userData.noMerge = 1; kn.add(kg);
    var sh = boneG(kn, 0, 0, 0);
    sh.add(limb2(D, 0.42*S, 1.00*S, [0,0,0]));                  // 脛：腓腸鼓、踝上收
    sh.add(plate(A, 0.45*S, 0.56*S, 0.23*S, [0, -0.28*S, -0.21*S], 0.14));
    sh.add(rib  (A, 0.07*S, 0.62*S, 0.07*S, [0, -0.30*S, 0.21*S], -0.06));
    sh.add(ram  ([ 0.20*S, -0.34*S, 0.14*S], 0.68*S, 0.11*S, 0.09*S));
    sh.add(ram  ([-0.20*S, -0.34*S, 0.14*S], 0.68*S, 0.11*S, 0.09*S));
    var an = boneG(sh, 0, -0.95*S, 0);
    an.add(joint(0.17*S, [0,0,0]));
    var ft = boneG(an, 0, 0, 0);
    ft.add(mesh (G.box, T, [0.58*S, 0.13*S, 1.12*S], [0, -0.10*S, 0.25*S]));
    ft.add(plate(A, 0.50*S, 0.14*S, 0.49*S, [0, -0.02*S, 0.34*S], -0.10));
    ft.add(rib  (A, 0.45*S, 0.07*S, 0.07*S, [0, 0.04*S, 0.56*S], -0.10));
    ft.add(bevel(D, 0.38*S, 0.16*S, 0.26*S, [0, -0.06*S, -0.29*S]));
    legs.push({hip:hip, th:th, sh:sh, ft:ft, kg:kg, s:sg});
  }

  /* ── 軀幹：前傾 8°、軀幹反向補 45°（脊椎的 S 形）── */
  var tor = boneG(body, 0, 0.22*S, 0);
  tor.rotation.set(0.14, 0, -0.052);
  /* 腰關節：不做實心腰柱 —— 胸與骨盆之間要看得穿 */
  tor.add(mesh(G.cyl, T, [0.30*S, 0.48*S, 0.30*S], [0, 0.04*S, 0]));
  tor.add(mesh(G.tor, T, [0.56*S, 0.56*S, 0.20*S], [0, 0.24*S, 0], [HP,0,0]));
  tor.add(ram ([ 0.25*S, 0.06*S, 0.16*S], 0.46*S, 0.10*S, 0.085*S, [-0.14,0,0]));
  tor.add(ram ([-0.25*S, 0.06*S, 0.16*S], 0.46*S, 0.10*S, 0.085*S, [-0.14,0,0]));
  tor.add(cable([0.26*S, 0.16*S, -0.22*S], 0.34*S));            // 只掛一邊 —— 刻意不對稱
  tor.add(bevel(D, 1.44*S, 1.02*S, 1.04*S, [0, 0.66*S, 0]));
  tor.add(plate(A, 0.86*S, 0.70*S, 0.36*S, [ 0.52*S, 0.88*S, 0.42*S], -0.26, -0.12));
  tor.add(plate(A, 0.86*S, 0.70*S, 0.36*S, [-0.52*S, 0.88*S, 0.42*S], -0.26,  0.12));
  tor.add(rib  (A, 0.13*S, 0.92*S, 0.18*S, [0, 0.84*S, 0.54*S], -0.24));   // 中央龍骨
  tor.add(plate(A, 0.28*S, 0.92*S, 0.76*S, [ 0.72*S, 0.50*S, 0.02*S], 0, -0.30, -0.16)); // 闊背→V錐
  tor.add(plate(A, 0.28*S, 0.92*S, 0.76*S, [-0.72*S, 0.50*S, 0.02*S], 0,  0.30,  0.16));
  tor.add(plate(A, 0.94*S, 0.30*S, 0.30*S, [0, 0.30*S, 0.40*S], 0.30));
  tor.add(seam (1.34*S, 0.05*S, 0.10*S, [0, 0.50*S, 0.48*S], 0.12));
  tor.add(plate(A, 1.30*S, 0.52*S, 0.28*S, [0, 0.90*S, -0.46*S], 0.12));
  tor.add(plate(A, 1.14*S, 0.42*S, 0.26*S, [0, 0.36*S, -0.50*S], 0.02));
  tor.add(bevel(D, 0.46*S, 0.66*S, 0.70*S, [ 0.96*S, 0.94*S, 0]));         // 肩承座
  tor.add(bevel(D, 0.46*S, 0.66*S, 0.70*S, [-0.96*S, 0.94*S, 0]));
  tor.add(plate(A, 0.70*S, 0.20*S, 0.62*S, [ 0.56*S, 1.05*S, 0], 0, -0.16, -0.30)); // 斜方肌斜面
  tor.add(plate(A, 0.70*S, 0.20*S, 0.62*S, [-0.56*S, 1.05*S, 0], 0,  0.16,  0.30));
  var core = mesh(G.oct, GLV, [0.36*S, 0.36*S, 0.36*S], [0, 0.68*S, 0.50*S]);
  core.userData.noMerge = 1; tor.add(core);
  tor.add(mesh(G.tor, T, [0.58*S, 0.58*S, 0.30*S], [0, 0.68*S, 0.56*S]));
  /* 外裝甲：P2 整片甩掉，剪影會變 */
  var SH = [[0, 0.88, 0.62, 0], [0.62, 0.70, 0.34, -0.5], [-0.62, 0.70, 0.34, 0.5], [0, 1.14, 0.10, 0.4]];
  for (i = 0; i < 4; i++){
    var a = SH[i], pl = boneG(tor, a[0]*S, a[1]*S, a[2]*S);
    pl.rotation.z = a[3];
    pl.add(plate(A, (i === 3 ? 0.90 : 0.50)*S, (i === 3 ? 0.24 : 0.66)*S, 0.11*S, [0,0,0], -0.2));
    pl.add(rib  (A, 0.04*S, (i === 3 ? 0.20 : 0.58)*S, 0.04*S, [ 0.18*S, 0, 0.06*S], -0.2));
    pl.add(seam ((i === 3 ? 0.86 : 0.46)*S, 0.03*S, 0.05*S, [0, 0, 0.07*S], -0.2));
    shed.push(pl);
  }
  /* 背包 + 四具可展開的砲莢 */
  var pack = boneG(tor, 0, 0.72*S, -0.58*S);
  pack.add(bevel(D, 0.80*S, 0.52*S, 0.36*S, [0, 0.08*S, -0.02*S]));
  pack.add(plate(A, 0.84*S, 0.18*S, 0.40*S, [0, 0.38*S, -0.04*S], -0.24));
  pack.add(rib  (A, 0.86*S, 0.08*S, 0.08*S, [0, 0.47*S, 0.13*S], -0.24));
  pack.add(seam (0.76*S, 0.05*S, 0.34*S, [0, 0.26*S, -0.02*S]));
  for (i = 0; i < 4; i++){
    var px = (i % 2 ? 1 : -1) * 0.28*S, py = (i < 2 ? 0.18 : -0.12)*S;
    var pd = boneG(pack, px, py, -0.16*S);
    pd.add(bevel(D, 0.20*S, 0.30*S, 0.20*S, [0, 0, -0.10*S]));
    pd.add(mesh(G.cyl, T, [0.13*S, 0.52*S, 0.13*S], [0, 0, -0.34*S], [HP,0,0]));
    var pm = mesh(G.tor, GLV, [0.22*S, 0.22*S, 0.55], [0, 0, -0.62*S]);
    pm.userData.noMerge = 1; pd.add(pm);
    pods.push(pd);
  }
  /* ── 頭：小（7.4 頭身）、偏左 0.14、掛在外露的頸柱上 ──
     《86》的 Legion 是「感測器叢裝在細頸上」，不是臉。 */
  var head = boneG(tor, 0.14*S, 1.06*S, 0.02*S);
  head.rotation.set(0.04, -0.22, 0.036);
  head.add(mesh(G.cyl, T, [0.22*S, 0.18*S, 0.22*S], [0, -0.10*S, 0]));       // 外露頸柱
  head.add(joint(0.13*S, [0, -0.10*S, 0]));
  head.add(bevel(D, 0.44*S, 0.30*S, 0.48*S, [0, 0.20*S, 0]));
  head.add(plate(A, 0.46*S, 0.15*S, 0.34*S, [0.02*S, 0.35*S, -0.02*S], -0.16, 0, -0.12));
  head.add(rib  (A, 0.48*S, 0.07*S, 0.07*S, [0.02*S, 0.43*S, 0.12*S], -0.30, 0, -0.12));
  var eye = mesh(G.cyl, GLA, [0.19*S, 0.11*S, 0.19*S], [-0.09*S, 0.20*S, 0.23*S], [HP,0,0]);
  eye.userData.noMerge = 1; head.add(eye);                                   // 偏心主鏡
  head.add(mesh(G.tor, T, [0.28*S, 0.28*S, 0.13*S], [-0.09*S, 0.20*S, 0.23*S]));
  var sub = mesh(G.box, GLA, [0.08*S, 0.05*S, 0.07*S], [0.13*S, 0.25*S, 0.23*S]);
  sub.userData.noMerge = 1; head.add(sub);                                   // 副感測群
  head.add(plate(A, 0.09*S, 0.26*S, 0.40*S, [0.24*S, 0.19*S, 0], 0.10, 0, 0.14));
  head.add(seam (0.34*S, 0.04*S, 0.06*S, [0.02*S, 0.06*S, 0.22*S]));
  head.add(mesh(G.cyl, T, [0.05*S, 0.62*S, 0.05*S], [-0.26*S, 0.50*S, -0.04*S], [0,0,0.30]));
  var ant = mesh(G.oct, GLA, [0.09*S, 0.09*S, 0.09*S], [-0.35*S, 0.76*S, -0.04*S]);
  ant.userData.noMerge = 1; head.add(ant);                                   // 感應珠（不對稱）
  /* ── 雙臂：肩甲上揚、兩段式上臂與前臂、肘部液壓 ── */
  for (i = 0; i < 2; i++){
    sg = i ? -1 : 1;
    var sdd = boneG(tor, sg*1.02*S, 0.94*S, 0);
    sdd.add(joint(0.23*S, [0,0,0], true));
    sdd.add(plate(A, 0.24*S, 0.88*S, 0.94*S, [-sg*0.40*S, 0.16*S, 0], 0.10, sg*0.18, -sg*0.22));
    sdd.add(rib  (A, 0.10*S, 0.10*S, 0.94*S, [-sg*0.44*S, 0.58*S, 0], 0.10, sg*0.18, -sg*0.22));
    sdd.add(seam (0.06*S, 0.72*S, 0.10*S, [-sg*0.42*S, 0.16*S, 0.30*S], 0.10, sg*0.18, -sg*0.22));
    var ua = boneG(sdd, 0, 0, 0);
    ua.add(limb2(D, 0.34*S, 0.88*S, [0,0,0]));
    ua.add(ram([sg*0.19*S, -0.52*S, -0.13*S], 0.52*S, 0.09*S, 0.075*S, [0.10,0,0]));
    var el = boneG(ua, 0, -0.88*S, 0);
    el.add(joint(0.19*S, [0,0,0]));
    var fa = boneG(el, 0, 0, 0);
    fa.rotation.x = -1.35;
    fa.add(limb2(D, 0.36*S, 0.80*S, [0,0,0]));
    fa.add(plate(A, 0.44*S, 0.62*S, 0.42*S, [0, -0.32*S, 0.12*S], 0));
    fa.add(rib  (A, 0.07*S, 0.66*S, 0.07*S, [ 0.19*S, -0.32*S, 0.16*S], 0));
    fa.add(rib  (A, 0.07*S, 0.66*S, 0.07*S, [-0.19*S, -0.32*S, 0.16*S], 0));
    if (i === 0){
      /* 《86》Morpho 的參考：一根不合理長的砲管。長度 15.6 單位 = 機體本身的 65%。 */
      fa.add(bevel(T, 0.30*S, 0.44*S, 0.30*S, [0, -0.94*S, 0]));
      fa.add(mesh(G.cyl, T, [0.19*S, 1.60*S, 0.19*S], [0, -1.60*S, 0]));
      fa.add(mesh(G.box, A, [0.13*S, 1.40*S, 0.10*S], [0, -1.60*S, 0.19*S]));
      fa.add(mesh(G.box, D, [0.12*S, 0.14*S, 0.20*S], [0, -1.10*S, 0.17*S]));
      fa.add(mesh(G.box, D, [0.12*S, 0.14*S, 0.20*S], [0, -2.16*S, 0.17*S]));
      var rail = mesh(G.box, GLA, [0.05*S, 1.20*S, 0.04*S], [0, -1.60*S, 0.25*S]);
      rail.userData.noMerge = 1; fa.add(rail);
      var mz = mesh(G.tor, GLA, [0.34*S, 0.34*S, 0.55], [0, -2.44*S, 0], [HP,0,0]);
      mz.userData.noMerge = 1; fa.add(mz);
      root.userData.mz = mz; root.userData.rail = rail;
    } else {
      fa.add(bevel(T, 0.30*S, 0.40*S, 0.30*S, [0, -0.88*S, 0]));
      fa.add(mesh(G.cyl, T, [0.16*S, 0.56*S, 0.16*S], [0, -1.20*S, 0]));
      fa.add(mesh(G.tor, D, [0.30*S, 0.30*S, 0.14*S], [0, -1.44*S, 0], [HP,0,0]));
    }
    arms.push({sd:sdd, ua:ua, fa:fa, s:sg});
  }
  root.userData.rig = {body:body, lower:lower, pel:pel, tor:tor, head:head, pack:pack,
    legs:legs, arms:arms, shed:shed, pods:pods, core:core, eye:eye, sub:sub, ant:ant,
    mz:root.userData.mz, rail:root.userData.rail, GLA:GLA, GL:GL, GLV:GLV};
  return mergeBones(root);
}
export function updKaiser(rig, o, dt){
  stages(rig, o, dt, 1.6);
  var S = KS, s1 = rig.s1, s2 = rig.s2, i, L, A;
  for (i = 0; i < 4; i++){
    rig.shed[i].visible = s1 < 0.5;                       // P2 甩甲
    rig.pods[i].visible = s1 > 0.4;                       // P2 同時展開背部砲台
    rig.pods[i].rotation.x = -1.15*s1 + o.tel*0.20 - o.atk*0.30;
    rig.pods[i].rotation.y = (i % 2 ? 1 : -1) * 0.42*s1;
  }
  /* P2 單膝跪地 —— 只跪左腿，右腿撐住。對稱的跪姿讀成投降，不對稱的讀成架砲。 */
  for (i = 0; i < 2; i++){
    L = rig.legs[i];
    var kneel = (i === 0) ? s1 : s1*0.34;
    var ph = (o.ph + i*0.5) % 1, sw = Math.sin(ph*Math.PI * 2)*o.mv*(1-s1)*(1-s2);
    L.th.rotation.x = -0.30 - 0.22*i + sw*0.26 + kneel*1.25 + s2*(0.5 + 0.4*i);
    L.sh.rotation.x =  0.66 + 0.28*i - Math.max(0, sw)*0.34 - kneel*1.70 - s2*0.9;
    L.ft.rotation.x = -(L.th.rotation.x + L.sh.rotation.x);     // 腳掌恆貼地
    L.kg.scale.setScalar(1 + 0.55*o.tel + 0.4*s1);
  }
  rig.body.position.y = 2.00*S - 4.60*s1 - 1.30*s2 + 0.16*Math.sin(o.ph*2*Math.PI * 2)*o.mv;
  rig.body.rotation.x = 0.05*o.mv + 0.20*s1 + 0.34*s2;
  rig.pel.rotation.z = 0.115 + 0.10*s1;                   // 骨盆傾角：重心偏移
  rig.lower.rotation.y = 0.20 - 0.10*s2;
  /* P3 上半身脫離：軀幹整個升起，下半身留在地上 */
  rig.tor.position.y = 0.22*S + 7.40*s2 + 0.40*Math.sin(o.t*1.1)*s2;
  rig.tor.rotation.x = 0.14 - 0.30*s1 - 0.20*s2 + o.tel*0.10 - o.atk*0.14;
  rig.tor.rotation.y = 0.30*Math.sin(o.t*0.7)*s2;
  rig.head.rotation.x = 0.04 - 0.10*o.tel;
  rig.head.rotation.y = -0.22 + 0.34*Math.sin(o.t*0.9);   // 頭獨立追瞄
  for (i = 0; i < 2; i++){
    A = rig.arms[i];
    A.sd.rotation.x = -0.12 - 0.55*s1 + 0.30*s2 + 0.10*Math.sin(o.t*1.2 + i*2);
    A.sd.rotation.z = A.s*(-0.22 - 0.34*s2);
    A.sd.rotation.y = A.s*(-0.26);
    A.ua.rotation.x = 0.30 + 0.60*s1 - o.tel*0.44 + o.atk*0.30 - 0.7*s2;
    A.fa.rotation.x = -1.35 - 0.30*s1 + o.atk*0.24;
  }
  rig.core.scale.setScalar((0.30 + 0.95*s1 + 0.30*s2)*(1 + 0.10*Math.sin(o.t*3) + 0.6*o.tel));
  rig.eye.scale.setScalar(1 + 0.20*Math.sin(o.t*2.2) + 0.9*o.tel);
  rig.sub.scale.setScalar(1 + 0.5*Math.sin(o.t*5.1) + 1.4*o.tel);
  rig.ant.scale.setScalar(1 + 0.3*Math.sin(o.t*1.7));
  if (rig.mz)   rig.mz.scale.setScalar(1 + 1.2*o.tel + 2.6*o.atk*(1-o.atk));
  if (rig.rail) rig.rail.scale.x = 0.05*KS*(1 + 3.4*o.tel);   // 軌道依序充能
  rig.GLA.emissiveIntensity = 2.2 + 3.0*o.tel + 6*o.atk*(1-o.atk);
  rig.GL.emissiveIntensity  = 2.0 + 2.2*s1 + 2.4*o.tel;
  rig.GLV.emissiveIntensity = 0.5 + 2.6*s1 + 2.4*s2 + 2.6*o.tel;
}